import * as React from 'react';
import { WorkspaceButton, WorkspaceDialog, WorkspaceNotice } from '@/components/ui/workspace';

/**
 * Delete an engagement (T-0161) — the typed-name confirmation.
 *
 * Deleting is a soft delete: the engagement is hidden for everyone, its
 * documents and history are kept, its search index is removed, and a firm
 * admin can restore it. The dialog says exactly that before anything
 * happens, and the delete button only enables once the engagement name is
 * typed (compared after trimming, the same rule the server applies).
 *
 * Presentational: the caller owns the request (`onConfirm`), the pending
 * state and the server's refusal. Built on the shared `WorkspaceDialog`,
 * like the retire dialogs in Firm settings.
 */

const FIELD =
  'block h-9 w-full rounded-card border border-ink-200 bg-paper-0 px-3 text-body text-ink-900 placeholder:text-ink-300 hover:border-ink-300 focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-accent-amber-600';

export interface DeleteEngagementModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  engagementName: string;
  clientName?: string;
  deleting: boolean;
  /** Server refusal, if the last attempt failed. */
  error: string | null;
  /** Called with the typed name once it matches. */
  onConfirm: (typedName: string) => void;
}

/** The same comparison the server makes. */
export function nameMatches(typed: string, engagementName: string): boolean {
  const t = typed.trim();
  return t.length > 0 && t === engagementName.trim();
}

export function DeleteEngagementModal({
  open,
  onOpenChange,
  engagementName,
  clientName,
  deleting,
  error,
  onConfirm,
}: DeleteEngagementModalProps) {
  const [typed, setTyped] = React.useState('');
  React.useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  const matches = nameMatches(typed, engagementName);
  const submit = () => {
    if (matches && !deleting) onConfirm(typed);
  };

  return (
    <WorkspaceDialog
      open={open}
      onOpenChange={(next) => {
        if (!deleting) onOpenChange(next);
      }}
      title={`Delete ${engagementName}?`}
      description={
        clientName
          ? `${clientName}. Firm admins only. A firm admin can restore it later.`
          : 'Firm admins only. A firm admin can restore it later.'
      }
      closeOnScrimClick={!deleting}
      actions={
        <>
          <WorkspaceButton size="md" disabled={deleting} onClick={() => onOpenChange(false)}>
            Keep the engagement
          </WorkspaceButton>
          <WorkspaceButton
            size="md"
            variant="destructive"
            disabled={!matches || deleting}
            onClick={submit}
          >
            {deleting ? 'Deleting…' : 'Delete engagement'}
          </WorkspaceButton>
        </>
      }
    >
      <div className="flex flex-col gap-3 text-body-sm text-ink-700">
        <div>
          <p className="font-semibold text-ink-900">What deleting does</p>
          <ul className="mt-1 list-disc pl-5">
            <li>
              Hides the engagement for everyone. It leaves the portfolio, the sidebar, client pages
              and notifications, and its pages no longer open.
            </li>
            <li>Removes its search index, so AI chat and search no longer cover its documents.</li>
          </ul>
        </div>
        <div>
          <p className="font-semibold text-ink-900">What it keeps</p>
          <ul className="mt-1 list-disc pl-5">
            <li>
              Its documents, registers, snapshots and full history are kept. Nothing is erased.
            </li>
            <li>
              A firm admin can restore it from Firm settings, Deleted engagements. Restoring brings
              it back for its team and rebuilds its search index.
            </li>
          </ul>
        </div>
      </div>
      <form
        className="flex flex-col"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <label
          htmlFor="delete-engagement-confirm"
          className="mb-1.5 block text-meta font-medium text-ink-700"
        >
          Type the engagement name to confirm
        </label>
        <input
          id="delete-engagement-confirm"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoFocus
          autoComplete="off"
          spellCheck={false}
          disabled={deleting}
          aria-describedby="delete-engagement-confirm-help"
          className={FIELD}
        />
        <p id="delete-engagement-confirm-help" className="mt-1.5 text-meta text-ink-500">
          Type <span className="font-mono text-ink-900">{engagementName.trim()}</span> exactly as
          shown.
        </p>
      </form>
      {error ? <WorkspaceNotice tone="critical">{error}</WorkspaceNotice> : null}
    </WorkspaceDialog>
  );
}
